import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const ctaButtonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 font-sans",
  {
    variants: {
      variant: {
        primary: "bg-prime-700 text-white hover:bg-prime-800 shadow-sm",
        secondary:
          "text-prime-700 border border-prime-500 hover:bg-prime-700 hover:text-white transition-all", // Matches ButtonStroke styles
        outline:
          "border border-prime-700 text-prime-700 bg-transparent hover:bg-prime-700 hover:text-white",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-[3.5rem] px-4 py-2 text-base", // Matches ButtonStroke default size
        sm: "h-9 rounded-md px-3",
        lg: "h-[3.5rem] rounded-md px-8 text-lg",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "default",
    },
  }
);

export interface CTAButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof ctaButtonVariants> {
  asChild?: boolean;
  icon?: React.ReactNode;
}

const CTAButton = React.forwardRef<HTMLButtonElement, CTAButtonProps>(
  (
    { className, variant, size, asChild = false, icon, children, ...props },
    ref
  ) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(ctaButtonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      >
        <div className="flex items-center justify-start w-full gap-2">
          {icon && <div className="w-[3rem] self-start shrink-0">{icon}</div>}
          <div className="w-full text-left flex justify-center items-center">
            {children}
          </div>
        </div>
      </Comp>
    );
  }
);
CTAButton.displayName = "CTAButton";

export { CTAButton, ctaButtonVariants };
